import { Autocomplete } from '@synoptic/ui-kit/autocomplete/autocomplete.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  comboboxListbox,
  comboboxOption,
} from '@synoptic/ui-kit/combobox/combobox.css.js';
import {
  input,
  inputContainer,
  inputVariants,
} from '@synoptic/ui-kit/input/input.css.js';
import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.js';
import { VisuallyHidden } from '@synoptic/ui-kit/visually-hidden/visually-hidden.js';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { href, useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';
import { Link } from '@/routes-utils/navigation';
import { UserInfo } from './user-info-types';
import {
  ctaWrappedSearchList,
  ctWrappedHeroCTAContainer,
  ctWrappedInput,
} from './route.css';

interface FormElements extends HTMLFormControlsCollection {
  username: HTMLInputElement;
}
interface UsernameFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

export const CtWrappedSearchCta = () => {
  const [usernameToPrefetch, setUsernameToPrefetch] = useState<string | null>(
    null,
  );

  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  const [deferedSearchValue] = useDebounce(searchValue, 200);

  const formRef = useRef<UsernameFormElement>(null);

  const { data: users = [], isLoading } = useQuery<string[]>({
    queryKey: ['ct-users-typeahead', deferedSearchValue],
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: () => {
      if (deferedSearchValue.trim().length === 0) {
        return Promise.resolve([]);
      }
      return fetch(
        href('/ct-users-typeahead') +
          `?q=${encodeURIComponent(deferedSearchValue)}`,
      ).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to load list of users');
        }
        return res
          .json()
          .then((data) => (data as UserInfo).map(([username]) => username));
      });
    },
  });

  const shouldRenderPopup = searchValue !== '';
  const isLaptop = useIsLaptop();

  return (
    <form
      ref={formRef}
      action={(formData) => {
        const username = ((formData.get('username') as string) || '').trim();

        if (username.length > 0) {
          navigate(`/ct-stats/${username}?card=intro`, {
            viewTransition: true,
          });
        }
      }}
      className={ctWrappedHeroCTAContainer}
    >
      <Autocomplete.Root
        items={users}
        value={searchValue}
        onValueChange={setSearchValue}
        filter={null}
      >
        <div
          className={clsx(
            inputContainer,
            inputVariants.default,
            ctWrappedInput,
          )}
        >
          <Autocomplete.Input
            name="username"
            placeholder="Enter username"
            className={input}
            minLength={1}
          />
        </div>
        {shouldRenderPopup && (
          <Autocomplete.Portal>
            <Autocomplete.Positioner
              side={isLaptop ? 'bottom' : 'top'}
              sideOffset={4}
            >
              <Autocomplete.Popup
                className={clsx(comboboxListbox, ctaWrappedSearchList)}
                aria-busy={isLoading || undefined}
              >
                <Autocomplete.List>
                  {(user) => (
                    <Autocomplete.Item
                      key={user}
                      className={comboboxOption}
                      value={user}
                      onClick={() => {
                        setUsernameToPrefetch(user);
                      }}
                    >
                      {user}
                    </Autocomplete.Item>
                  )}
                </Autocomplete.List>
              </Autocomplete.Popup>
            </Autocomplete.Positioner>
          </Autocomplete.Portal>
        )}
      </Autocomplete.Root>

      <Button type="submit" variant="primary">
        Search
      </Button>

      {usernameToPrefetch && (
        <VisuallyHidden>
          <Link
            to={href('/ct-stats/:username', { username: usernameToPrefetch })}
            prefetch="render"
          ></Link>
        </VisuallyHidden>
      )}
    </form>
  );
};
